import { $codeWords } from '@/stores';
import { closeDialog } from '@/stores/dialog';
import clsx from 'clsx';
import { useState } from 'react';

export type ReportCodeWords = Record<string, string>;

export function CodeWordsEditor() {
  const [editedCodeWords, setEditedCodeWords] = useState<ReportCodeWords>(
    $codeWords.get(),
  );
  const [newCodeWordName, setNewCodeWordName] = useState('');
  const [newCodeWordContent, setNewCodeWordContent] = useState('');

  const handleSave = () => {
    $codeWords.set(editedCodeWords);
    closeDialog();
  };

  const handleUpdateCodeWord = (name: string, content: string) => {
    setEditedCodeWords(prev => ({
      ...prev,
      [name]: content,
    }));
  };

  const handleDeleteCodeWord = (name: string) => {
    setEditedCodeWords(prev => {
      const newCodeWords = { ...prev };
      delete newCodeWords[name];
      return newCodeWords;
    });
  };

  const handleAddCodeWord = () => {
    if (!newCodeWordName.trim()) return;

    const normalizedName = newCodeWordName.trim().toUpperCase();
    setEditedCodeWords(prev => ({
      ...prev,
      [normalizedName]: newCodeWordContent,
    }));
    setNewCodeWordName('');
    setNewCodeWordContent('');
  };

  return (
    <div
      className="stop-scrolling fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={closeDialog}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <h2 className="text-2xl font-semibold text-gray-900">
            Edit Report CodeWords
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Create and customize codewords for quick access
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Existing CodeWords */}
          <table className="w-full">
            <thead>
              <tr>
                <th className="w-0 text-nowrap">Code Word</th>
                <th>Meaning</th>
                <th className="w-0">Delete</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(editedCodeWords).map(([name, content]) => (
                <tr
                  key={name}
                  className="rounded-lg border border-gray-300 p-4"
                >
                  <td className="px-2 text-center">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {name}
                    </h3>
                  </td>
                  <td>
                    <input
                      type="text"
                      value={content}
                      onChange={e => handleUpdateCodeWord(name, e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="CodeWord content..."
                    />
                  </td>
                  <td>
                    <button
                      onClick={() => handleDeleteCodeWord(name)}
                      className="cursor-pointer rounded px-2 py-1 text-sm font-medium text-red-600 transition-colors hover:bg-red-100"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Add New CodeWord */}
          <div className="mt-6 border-t border-gray-300 pt-6">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">
              Add New CodeWord
            </h3>
            <div className="space-y-3">
              <div>
                <label
                  htmlFor="codeword-name"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Code Word
                </label>
                <input
                  id="codeword-name"
                  type="text"
                  value={newCodeWordName}
                  onChange={e => setNewCodeWordName(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="e.g., NODUFF"
                />
              </div>
              <div>
                <label
                  htmlFor="codeword-content"
                  className="mb-1 block text-sm font-medium text-gray-700"
                >
                  Meaning
                </label>
                <input
                  type="text"
                  id="codeword-content"
                  value={newCodeWordContent}
                  onChange={e => setNewCodeWordContent(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Meaning..."
                />
              </div>
              <button
                onClick={handleAddCodeWord}
                disabled={!newCodeWordName.trim()}
                className={clsx(
                  'rounded-md px-4 py-2 font-medium',
                  newCodeWordName.trim()
                    ? 'cursor-pointer bg-green-600 text-white hover:bg-green-700'
                    : 'cursor-not-allowed bg-gray-300 text-gray-500',
                )}
              >
                Add CodeWord
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 border-t border-gray-200 px-6 py-4">
          <button
            onClick={closeDialog}
            className="cursor-pointer rounded-md border border-gray-300 px-4 py-2 font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="cursor-pointer rounded-md bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700"
          >
            Save CodeWords
          </button>
        </div>
      </div>
    </div>
  );
}
